import * as XLSX from 'xlsx';

// Headers sit in row 2 and data from row 3 (0-based rows 1 and 2), the same
// layout sheet_to_json(sheet, { range: 1 }) reads in AddClientModal.
const HEADER_ROW = 1;
const FIRST_DATA_ROW = 2;

function sheetRange(sheet) {
  if (!sheet?.['!ref'] || typeof XLSX.utils?.decode_range !== 'function') return null;
  try {
    return XLSX.utils.decode_range(sheet['!ref']);
  } catch (error) {
    return null;
  }
}

/**
 * Each data row's hyperlink targets, by header: [{ [header]: target }, ...],
 * index-aligned with sheet_to_json(sheet, { range: 1, blankrows: true }).
 * sheet_to_json keeps a cell's visible text and drops its link, so a broker
 * address that exists only as a mailto on the name, or a flyer URL behind the
 * word "Flyer", is otherwise lost at upload.
 */
export function readRowHyperlinks(sheet, rowCount = 0) {
  const range = sheetRange(sheet);
  if (!range) return [];

  const headers = {};
  for (let column = range.s.c; column <= range.e.c; column += 1) {
    const header = sheet[XLSX.utils.encode_cell({ r: HEADER_ROW, c: column })]?.v;
    if (header !== undefined && header !== null && String(header).trim()) {
      headers[column] = String(header);
    }
  }

  const rows = [];
  for (let index = 0; index < rowCount; index += 1) {
    const links = {};
    Object.entries(headers).forEach(([column, header]) => {
      const cell = sheet[XLSX.utils.encode_cell({ r: FIRST_DATA_ROW + index, c: Number(column) })];
      const target = cell?.l?.Target;
      if (typeof target === 'string' && target.trim()) links[header] = target.trim();
    });
    rows.push(Object.keys(links).length > 0 ? links : null);
  }
  return rows;
}

// The divider the backend writes into a campaign Sheet when a property is
// ruled out (EmailAutomation sheet_operations.py ensure_nonviable_divider:
// "NON-VIABLE" in column A only, found with strip().upper()). Rows below it
// were ruled out in that campaign.
export const NON_VIABLE_DIVIDER_TEXT = 'NON-VIABLE';

/**
 * The data-row index (as sheet_to_json(sheet, { range: 1 }) numbers rows) of
 * the first NON-VIABLE divider in column A, or -1. A downloaded SiteSift Sheet
 * uploaded again still has it, with the ruled-out brokers' addresses below.
 */
export function findNonViableDividerIndex(sheet, rowCount = 0) {
  const range = sheetRange(sheet);
  if (!range) return -1;
  for (let index = 0; index < rowCount; index += 1) {
    const value = sheet[XLSX.utils.encode_cell({ r: FIRST_DATA_ROW + index, c: 0 })]?.v;
    if (String(value ?? '').trim().toUpperCase() === NON_VIABLE_DIVIDER_TEXT) return index;
  }
  return -1;
}

// A downloaded SiteSift Sheet uploaded again as a new campaign carries the old
// campaign's hidden AI_META tab (per-cell provenance of the AI's writes). The
// Sheet-creation route copies every tab and the backend reuses an existing
// AI_META tab, so the new campaign would start from the old provenance, which
// feeds the overwrite guard. It is removed before the workbook leaves the
// browser; the backend creates a fresh one on its first write.
const CARRIED_OVER_AUTOMATION_TABS = new Set(['ai_meta']);

export function removeCarriedOverAutomationTabs(workbook) {
  const names = Array.isArray(workbook?.SheetNames) ? workbook.SheetNames : [];
  const removed = names.filter(name => CARRIED_OVER_AUTOMATION_TABS.has(String(name || '').trim().toLowerCase()));
  if (removed.length === 0) return [];

  const kept = [];
  const keptSheetMeta = [];
  const sheetMeta = Array.isArray(workbook.Workbook?.Sheets) ? workbook.Workbook.Sheets : null;
  names.forEach((name, index) => {
    if (removed.includes(name)) {
      delete workbook.Sheets[name];
      return;
    }
    kept.push(name);
    if (sheetMeta) keptSheetMeta.push(sheetMeta[index]);
  });
  workbook.SheetNames = kept;
  if (sheetMeta) workbook.Workbook.Sheets = keptSheetMeta;
  return removed;
}

// Tabs the product itself reads or writes; never property lists.
const NON_PROPERTY_TABS = new Set(['script', 'log', 'ai_meta']);

function countDataRows(sheet) {
  const range = sheetRange(sheet);
  if (!range) return 0;
  let count = 0;
  for (let row = Math.max(FIRST_DATA_ROW, range.s.r); row <= range.e.r; row += 1) {
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const value = sheet[XLSX.utils.encode_cell({ r: row, c: column })]?.v;
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        count += 1;
        break;
      }
    }
  }
  return count;
}

/**
 * Tabs other than the one being emailed that hold rows (a FOR SALE tab, a
 * second property list). Every tab is copied into the campaign Sheet, but only
 * the emailed tab's rows are ever contacted, so the advisor is told.
 */
export function describeUnemailedTabs(workbook, emailedSheetName) {
  const names = Array.isArray(workbook?.SheetNames) ? workbook.SheetNames : [];
  return names
    .filter(name => name !== emailedSheetName)
    .filter(name => !NON_PROPERTY_TABS.has(String(name || '').trim().toLowerCase()))
    .map(name => ({ name, rowCount: countDataRows(workbook.Sheets?.[name]) }))
    .filter(tab => tab.rowCount > 0);
}
